import React, { createContext, useContext } from 'react';

import {
  DefaultApi as TrackApi,
  Configuration as TrackApiConfiguration,
} from '@rtkwlf/track-public';

import { env } from '@packages/utils/env';

import type { Pod } from './types';

/* -------------------------------------------------------------------------------------------------
 * TrackApiContext
 * -----------------------------------------------------------------------------------------------*/
type TrackApiContextProps = {
  /**
   * A record of all api clients keyed by pod
   */
  client: TrackApi;

  /**
   * organization id
   */
  orgID: string;

  /**
   * Current pod for organization
   */
  pod: Pod;
};

const TrackApiContext = createContext<TrackApiContextProps | undefined>(
  undefined,
);

/**
 * Gets the track api client for the provided pod
 *
 * @param pod pod to return the api client for
 * @returns api client for the provided pod
 */
const useTrackApi = () => {
  const context = useContext(TrackApiContext);

  if (context == null) {
    throw new Error(
      'Component must be wrapped in TrackApiContext in order to access context.',
    );
  }

  return context;
};

/* -------------------------------------------------------------------------------------------------
 * TrackApiProvider
 * -----------------------------------------------------------------------------------------------*/
type TrackApiProviderProps = {
  /**
   * An authenticated fetch function. It is expected that this function adheres to the Fetch API as defined
   * in https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API and provides the necessary authenticated methods
   * with Auth0
   */
  fetch: typeof fetch;

  /**
   * organization id
   */
  orgID: TrackApiContextProps['orgID'];

  /**
   * Current pod for organization
   */
  pod: TrackApiContextProps['pod'];
};

/**
 * # TrackApiProvider
 *
 * Top-level data fetching provider for focus track api
 *
 * @example
 * ```tsx
 * <TrackApiProvider fetch={fetch} orgID="org-id" pod="us001">
 *   <App />
 * </TrackApiProvider>
 * ```
 */
const TrackApiProvider: React.FunctionComponent<
  React.PropsWithChildren<TrackApiProviderProps>
> = ({ children, fetch, orgID, pod }) => {
  /**
   * Create track client for pod url
   */
  const client = React.useMemo(() => {
    return new TrackApi(
      new TrackApiConfiguration({
        fetchApi: fetch,
        // aaho: I cannot seem to figure out a way to load `.env` in playwright component tests. I also tried
        // using vites `define` in the `ctViteConfig` but that didnt work either. So for now, the fallback url
        // is here purely in service of component tests.
        // TODO S2CX-7189 @icebreakers
        basePath: (
          env.VITE_TRACK_PUBLIC_BASE_PATH ?? 'https://track-public-mock'
        ).replace('{{pod}}', pod),
      }),
    );
  }, [fetch, pod]);

  return (
    <TrackApiContext.Provider value={{ client, orgID, pod }}>
      {children}
    </TrackApiContext.Provider>
  );
};

export { TrackApiProvider, useTrackApi };
export type { TrackApiProviderProps };
